import { useT, type MessageKey } from '../lib/i18n';
import {
  CHOICE_KEY, FIELD_KEY, choiceOf,
  PARTY_ROLES, type ChoiceView, type FieldClass, type FieldDefinition, type FieldRole, type FieldType
} from '../lib/registry';

/** The datatypes a person may give a sector's card field in P2. A reference, a file and a document of its own are links and files (P3, P5). */
export const AUTHORED_TYPES: FieldType[] = ['text', 'integer', 'decimal', 'money', 'quantity', 'boolean', 'date', 'timestamp', 'choice'];
/** `premium`: what only a company that has paid for it may read (GeoNexora's licensed data). */
const CLASSES: FieldClass[] = ['none', 'money', 'contact', 'pii', 'premium'];

/** The two languages a definition is authored in here; the others it holds are kept as they are. */
const AUTHORED_LANGUAGES = ['en', 'tr'] as const;

/**
 * One field of a sector's card, as the Types screen authors it (docs/en/36 sec.4.4-4.7).
 *
 * <p>What it lets a person write is what the registry accepts, so that a refusal is rare
 * and, when it comes, about something this screen cannot know - instances already stored.
 * A unit only on a quantity, several values only on a choice or a text, a role only on a
 * type that can play it, choices as keys with labels: the controls appear when they mean
 * something and not otherwise.
 *
 * <p>A field that has been published keeps its key: what an instance stores under a key
 * is there for ever, and a rename is a mapping (doc 32 sec.6), not a typo fixed.
 */
export function FieldEditor({ field, published, problem, onChange, onRemove, wording = 'card', kinds, targets, valueTypes, uses }: {
  field: FieldDefinition;
  /** The datatypes it may be given here; a sector's card field by default. The taxonomy screen gives all of them. */
  kinds?: FieldType[];
  /** What a reference may refer to: the templates there are (docs/en/43). */
  targets?: { key: string; label: string }[];
  /** What a value field may hold: the value types there are (docs/en/43 sec.5). */
  valueTypes?: { key: string; label: string }[];
  /** Where the field is used by a directive: what taking it away would break (docs/en/43 D10). */
  uses?: { where: string; what: string }[];
  /** Whose field it is: a card's, said in terms of the board; or a company's record's (docs/en/42). */
  wording?: 'card' | 'record';
  /** Whether a published version has this field already: its key is then fixed. */
  published: boolean;
  problem?: string;
  onChange: (next: FieldDefinition) => void;
  onRemove: () => void;
}) {
  const { t } = useT();
  const set = (patch: Partial<FieldDefinition>) => onChange(tidy({ ...field, ...patch }));
  const choices = (field.choices ?? []).map(choiceOf);
  const keyBad = field.key !== '' && !FIELD_KEY.test(field.key);
  const offered = kinds ?? AUTHORED_TYPES;

  return (
    <div className={`type-field${problem ? ' has-problem' : ''}`} data-testid={`type-field-${field.key || 'new'}`}>
      <div className="field-row">
        <label className="field">
          <span>{t('types.fieldKey')}</span>
          <input value={field.key} readOnly={published} spellCheck={false} data-testid="field-key"
                 title={published ? t('types.keyFixed') : t('types.keyHint')}
                 onChange={(e) => set({ key: e.target.value.trim() })} />
          {keyBad && <span className="hint problem">{t('types.keyShape')}</span>}
        </label>
        <label className="field">
          <span>{t('types.fieldType')}</span>
          <select value={field.type} data-testid="field-type" disabled={published}
                  title={published ? t('types.typeFixed') : undefined}
                  onChange={(e) => set({ type: e.target.value as FieldType })}>
            {offered.map((type) => (
              <option key={type} value={type}>{t(`types.type.${type}` as MessageKey, { type: '…' })}</option>
            ))}
            {!offered.includes(field.type) && <option value={field.type}>{field.type}</option>}
          </select>
        </label>
        {AUTHORED_LANGUAGES.map((language) => (
          <label className="field" key={language}>
            <span>{t('types.labelIn', { language: language.toUpperCase() })}</span>
            <input value={field.labels?.[language] ?? ''} data-testid={`field-label-${language}`}
                   onChange={(e) => set({ labels: { ...(field.labels ?? {}), [language]: e.target.value } })} />
          </label>
        ))}
      </div>

      <div className="field-row type-field-flags">
        <label className="field-check">
          <input type="checkbox" checked={field.required ?? false} data-testid="field-required"
                 onChange={(e) => set({ required: e.target.checked })} />
          {t('types.required')}
        </label>
        <label className="field-check">
          <input type="checkbox" checked={field.filterable ?? false} data-testid="field-filterable"
                 onChange={(e) => set({ filterable: e.target.checked })} />
          {t(wording === 'record' ? 'types.filterableRecord' : 'types.filterable')}
        </label>
        {field.type === 'reference' && targets && (
          <label className="field">
            <span>{t('taxonomy.refersTo')}</span>
            <select value={field.refersTo ?? ''} data-testid="field-refers-to" disabled={published}
                    onChange={(e) => set({ refersTo: e.target.value || undefined })}>
              <option value="">{t('taxonomy.choose')}</option>
              {targets.map((target) => <option key={target.key} value={target.key}>{target.label}</option>)}
            </select>
          </label>
        )}
        {field.type === 'value' && valueTypes && (
          <label className="field">
            <span>{t('taxonomy.holds')}</span>
            <select value={field.of ?? ''} data-testid="field-of" disabled={published}
                    onChange={(e) => set({ of: e.target.value || undefined })}>
              <option value="">{t('taxonomy.choose')}</option>
              {valueTypes.map((value) => <option key={value.key} value={value.key}>{value.label}</option>)}
            </select>
          </label>
        )}
        {(field.type === 'choice' || field.type === 'text' || field.type === 'value') && (
          <label className="field-check">
            <input type="checkbox" checked={field.many ?? false} data-testid="field-many" disabled={published}
                   onChange={(e) => set({ many: e.target.checked })} />
            {t('types.many')}
          </label>
        )}
        <label className="field">
          <span>{t('types.fieldClass')}</span>
          <select value={field.fieldClass ?? 'none'} data-testid="field-class"
                  onChange={(e) => set({ fieldClass: e.target.value as FieldClass })}>
            {CLASSES.map((c) => <option key={c} value={c}>
              {t(wording === 'record' && c === 'none' ? 'types.class.noneRecord' : `types.class.${c}` as MessageKey)}</option>)}
          </select>
        </label>
        {(field.type === 'money' || field.type === 'date' || field.type === 'timestamp' || field.type === 'geometry'
          || field.type === 'reference' || (field.type === 'text' && !field.many)) && (
          <label className="field">
            <span>{t(wording === 'record' ? 'types.roleRecord' : 'types.role')}</span>
            <select value={field.role ?? ''} data-testid="field-role"
                    onChange={(e) => set({ role: (e.target.value || undefined) as FieldRole | undefined })}>
              <option value="">{t('types.role.none')}</option>
              {field.type === 'money' && <option value="value">{t('types.role.value')}</option>}
              {(field.type === 'date' || field.type === 'timestamp') && <option value="due">{t('types.role.due')}</option>}
              {/* The shape a map layer draws (docs/en/60 sec.3). */}
              {field.type === 'geometry' && <option value="shape">{t('types.role.shape')}</option>}
              {field.type === 'text' && <option value="title">{t(wording === 'record' ? 'types.role.titleRecord' : 'types.role.title')}</option>}
              {/* The parts a relation plays in the work (docs/en/41 sec.5), each played by a reference. */}
              {field.type === 'reference' && PARTY_ROLES.map((role) => (
                <option key={role} value={role}>{t(`types.role.${role}` as MessageKey)}</option>
              ))}
            </select>
          </label>
        )}
        {field.type === 'quantity' && (
          <label className="field">
            <span>{t('types.unit')}</span>
            <input value={field.unit ?? ''} placeholder={t('types.unitExample')} data-testid="field-unit" readOnly={published}
                   onChange={(e) => set({ unit: e.target.value.trim() || undefined })} />
          </label>
        )}
        {field.type !== 'choice' && field.type !== 'value' && field.type !== 'json' && !field.many && (
          <label className="field">
            <span>{t('types.default')}</span>
            <input value={field.defaultValue ?? ''} data-testid="field-default"
                   placeholder={field.type === 'boolean' ? t('types.booleanDefault') : ''}
                   onChange={(e) => set({ defaultValue: e.target.value === '' ? undefined : e.target.value })} />
          </label>
        )}
        <span className="spacer" />
        <button className="ghost danger" data-testid="field-remove" onClick={onRemove}
                title={uses && uses.length > 0 ? t('taxonomy.removeUsed') : published ? t('types.removePublishedHint') : undefined}>
          {t('types.removeField')}
        </button>
      </div>

      {field.type === 'choice' && (
        <ChoiceList choices={choices} onChange={(next) => set({ choices: next })} />
      )}
      {uses && uses.length > 0 && (
        <p className="hint" data-testid="field-uses">
          {t('taxonomy.usedBy', { uses: uses.map((u) => `${u.where} (${u.what})`).join(', ') })}
        </p>
      )}
      {problem && <p className="hint problem" data-testid="field-problem">{problem}</p>}
    </div>
  );
}

function ChoiceList({ choices, onChange }: { choices: ChoiceView[]; onChange: (next: ChoiceView[]) => void }) {
  const { t } = useT();
  const update = (index: number, patch: Partial<ChoiceView>) =>
    onChange(choices.map((choice, at) => (at === index ? { ...choice, ...patch } : choice)));
  return (
    <div className="choice-list" data-testid="field-choices">
      <span className="hint">{t('types.choicesHint')}</span>
      {choices.map((choice, index) => (
        <div className="field-row" key={index}>
          <label className="field">
            <span>{t('types.choiceKey')}</span>
            <input value={choice.key} spellCheck={false} data-testid={`choice-key-${index}`}
                   onChange={(e) => update(index, { key: e.target.value.trim() })} />
            {choice.key !== '' && !CHOICE_KEY.test(choice.key) && <span className="hint problem">{t('types.choiceShape')}</span>}
          </label>
          {AUTHORED_LANGUAGES.map((language) => (
            <label className="field" key={language}>
              <span>{t('types.labelIn', { language: language.toUpperCase() })}</span>
              <input value={choice.labels?.[language] ?? ''} data-testid={`choice-label-${language}-${index}`}
                     onChange={(e) => update(index, { labels: { ...(choice.labels ?? {}), [language]: e.target.value } })} />
            </label>
          ))}
          <button className="ghost" onClick={() => onChange(choices.filter((_, at) => at !== index))}>
            {t('types.removeChoice')}
          </button>
        </div>
      ))}
      <button data-testid="choice-add" onClick={() => onChange([...choices, { key: '', labels: {} }])}>
        {t('types.addChoice')}
      </button>
    </div>
  );
}

/**
 * Leaves out what does not apply, so that the definition says only what it means: no unit
 * on a number that is not a quantity, no several on a date, empty labels left out. A
 * definition that carried "unit: m" on a date would be refused, and rightly.
 */
function tidy(field: FieldDefinition): FieldDefinition {
  const out: FieldDefinition = { ...field };
  if (out.type !== 'quantity') delete out.unit;
  if (out.type !== 'choice') delete out.choices;
  if (out.type !== 'choice' && out.type !== 'text' && out.type !== 'value') delete out.many;
  if (out.type !== 'reference') delete out.refersTo;
  if (out.type !== 'value') delete out.of;
  if (!out.many) delete out.many;
  if (!out.required) delete out.required;
  if (!out.filterable) delete out.filterable;
  if (out.fieldClass === 'none') delete out.fieldClass;
  if (out.role === 'value' && out.type !== 'money') delete out.role;
  if (out.role === 'due' && out.type !== 'date' && out.type !== 'timestamp') delete out.role;
  if (out.role === 'title' && (out.type !== 'text' || out.many)) delete out.role;
  if (out.role === 'shape' && out.type !== 'geometry') delete out.role;
  if (out.role && PARTY_ROLES.includes(out.role) && out.type !== 'reference') delete out.role;
  if (out.many) delete out.defaultValue;
  if (out.labels) {
    const labels = Object.fromEntries(Object.entries(out.labels).filter(([, v]) => v.trim() !== ''));
    if (Object.keys(labels).length === 0) delete out.labels; else out.labels = labels;
  }
  if (out.choices) {
    out.choices = out.choices.map(choiceOf).map((choice) => {
      const labels = Object.fromEntries(Object.entries(choice.labels ?? {}).filter(([, v]) => v.trim() !== ''));
      return Object.keys(labels).length === 0 ? { key: choice.key } : { key: choice.key, labels };
    });
  }
  return out;
}
